import React from 'react'
import { Globe, Share2, ExternalLink } from 'lucide-react'

interface WebsitePreviewContentProps {
  data: {
    title?: string
    description?: string
    target_url?: string
    favicon?: string
    buttonText?: string
    fonts?: {
      heading?: string
      body?: string
    }
    textSizes?: {
      title?: string
      description?: string
      url?: string
    }
    companyPosition?: 'top' | 'bottom'
  }
  branding: {
    companyName?: string
    textColor?: string
    primaryColor?: string
    secondaryColor?: string
    brandingType?: 'beyondpages' | 'custom' | 'none'
  }
  bgGradient?: string
  colorMode?: 'gradient' | 'structured'
  isPreview?: boolean
}

export const WebsitePreviewContent: React.FC<WebsitePreviewContentProps> = ({ 
  data, 
  branding,
  bgGradient = 'linear-gradient(to bottom right, #3B82F6, #1F2937)',
  colorMode = 'gradient',
  isPreview = true
}) => {
  // Helper function to calculate text color based on background
  const getTextColorForBg = (bgColor: string) => {
    const color = bgColor.replace('#', '')
    const r = parseInt(color.substr(0, 2), 16)
    const g = parseInt(color.substr(2, 2), 16)
    const b = parseInt(color.substr(4, 2), 16)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000
    return brightness > 128 ? '#1F2937' : '#FFFFFF'
  }

  const primaryTextColor = colorMode === 'structured' 
    ? getTextColorForBg(branding.primaryColor || '#3B82F6')
    : (branding.textColor || 'white')
  
  const secondaryTextColor = colorMode === 'structured'
    ? getTextColorForBg(branding.secondaryColor || '#1F2937')
    : (branding.textColor || 'white')

  // Extract domain from URL for cleaner display
  const getDomainFromUrl = (url: string) => {
    try {
      const domain = new URL(url).hostname
      return domain.replace('www.', '')
    } catch {
      return url
    }
  }

  return (
    <div className={`h-full flex flex-col bg-gray-50 ${isPreview ? 'pt-14 sm:pt-4' : 'pt-0'}`}>
      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {colorMode === 'gradient' ? (
          // Gradient mode - single continuous gradient
          <div className="min-h-full relative" style={{
            background: bgGradient || `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`
          }}>
            {/* Animated background particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gray-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
            </div>
            
            {/* Content container with same structure as structured mode */}
            <div className="min-h-full flex flex-col">
              {/* Header section - 65% */}
              <div 
                className="px-6 relative"
                style={{
                  minHeight: '65%',
                  paddingTop: '2rem',
                  paddingBottom: '6rem'
                }}
              >
                {/* Content wrapper */}
                <div className="h-full flex flex-col">
                  {/* Company name at top for custom branding (only when position is top) */}
                  {branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'top' && (
                    <div className="text-center mb-4">
                      <h2 className="text-lg font-semibold" style={{ 
                        color: branding.textColor,
                        fontFamily: data.fonts?.heading || 'Inter'
                      }}>
                        {branding.companyName}
                      </h2>
                    </div>
                  )}

                  {/* Globe Icon with favicon overlay */}
                  <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                    {data.favicon ? (
                      <img src={data.favicon} alt="Favicon" className="w-12 h-12 object-contain" />
                    ) : (
                      <Globe className="w-10 h-10 text-white" />
                    )}
                  </div>

                  {/* Title */}
                  {data.title && (
                    <h1 className={`${data.textSizes?.title || 'text-2xl'} font-bold text-center mb-3`} style={{ 
                      color: branding.textColor,
                      fontFamily: data.fonts?.heading || 'Inter'
                    }}>
                      {data.title}
                    </h1>
                  )}

                  {/* Description */}
                  {data.description && (
                    <p className={`${data.textSizes?.description || 'text-base'} text-center opacity-90 whitespace-pre-wrap`} style={{ 
                      color: branding.textColor,
                      fontFamily: data.fonts?.body || 'Inter'
                    }}>
                      {data.description}
                    </p>
                  )}
                  
                  {/* Spacer */}
                  <div className="flex-1"></div>
                </div>
              </div>

              {/* Body section - 35% */}
              <div className="flex-1 px-6 py-8">
                <div className="max-w-sm mx-auto" style={{ marginTop: '-5rem' }}>
                  {/* URL Preview Box */}
                  <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      {data.favicon && (
                        <img src={data.favicon} alt="Favicon" className="w-5 h-5 object-contain mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`${data.textSizes?.url || 'text-sm'} font-medium text-gray-800 truncate`}>
                          {getDomainFromUrl(data.target_url || 'https://example.com')}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {data.target_url || 'https://example.com'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Visit Website Button */}
                  <button 
                    className="w-full px-6 py-3 rounded-full font-medium flex items-center justify-center gap-2 shadow-lg transition-transform hover:scale-105"
                    style={{ 
                      backgroundColor: branding.primaryColor || '#3B82F6',
                      color: branding.textColor || 'white',
                      fontFamily: data.fonts?.body || 'Inter'
                    }}
                  >
                    <ExternalLink className="w-5 h-5" />
                    {data.buttonText || 'Website besuchen'}
                  </button>

                  {/* Company name at bottom */}
                  {branding.brandingType !== 'none' && data.companyPosition === 'bottom' && (
                    <div className="text-center mt-8">
                      {branding.brandingType === 'beyondpages' ? (
                        <p className="text-sm opacity-70" style={{ 
                          color: branding.textColor,
                          fontFamily: data.fonts?.body || 'Inter'
                        }}>
                          Powered by BeyondPages
                        </p>
                      ) : branding.brandingType === 'custom' && branding.companyName ? (
                        <h2 className="text-lg font-semibold" style={{ 
                          color: branding.textColor,
                          fontFamily: data.fonts?.body || 'Inter'
                        }}>
                          {branding.companyName}
                        </h2>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Structured mode - 65/35 split
          <div className="min-h-full flex flex-col relative">
            {/* Header section - 65% */}
            <div 
              className="px-6 relative"
              style={{
                backgroundColor: branding.primaryColor,
                minHeight: '65%',
                paddingTop: '2rem',
                paddingBottom: '6rem'
              }}
            >
              {/* Content wrapper */}
              <div className="h-full flex flex-col">
                {/* Company name at top for custom branding (only when position is top) */}
                {branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'top' && (
                  <div className="text-center mb-4">
                    <h2 className="text-lg font-semibold" style={{ 
                      color: primaryTextColor,
                      fontFamily: data.fonts?.heading || 'Inter'
                    }}>
                      {branding.companyName}
                    </h2>
                  </div>
                )}

                {/* Globe Icon with favicon overlay */}
                <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                  {data.favicon ? (
                    <img src={data.favicon} alt="Favicon" className="w-12 h-12 object-contain" />
                  ) : (
                    <Globe className="w-10 h-10" style={{ color: primaryTextColor }} />
                  )}
                </div>

                {/* Title */}
                {data.title && (
                  <h1 className={`${data.textSizes?.title || 'text-2xl'} font-bold text-center mb-3`} style={{ 
                    color: primaryTextColor,
                    fontFamily: data.fonts?.heading || 'Inter'
                  }}>
                    {data.title}
                  </h1>
                )}

                {/* Description */}
                {data.description && (
                  <p className={`${data.textSizes?.description || 'text-base'} text-center opacity-90 whitespace-pre-wrap`} style={{ 
                    color: primaryTextColor,
                    fontFamily: data.fonts?.body || 'Inter'
                  }}>
                    {data.description}
                  </p>
                )}
                
                {/* Spacer */}
                <div className="flex-1"></div>
              </div>
            </div>

            {/* Body section - 35% */}
            <div 
              className="flex-1 px-6 py-8"
              style={{
                backgroundColor: branding.secondaryColor,
                color: secondaryTextColor
              }}
            >
              <div className="max-w-sm mx-auto" style={{ marginTop: '-3rem' }}>
                {/* URL Preview Box */}
                <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    {data.favicon && (
                      <img src={data.favicon} alt="Favicon" className="w-5 h-5 object-contain mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`${data.textSizes?.url || 'text-sm'} font-medium text-gray-800 truncate`}>
                        {getDomainFromUrl(data.target_url || 'https://example.com')}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {data.target_url || 'https://example.com'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Visit Website Button */}
                <button 
                  className="w-full px-6 py-3 rounded-full font-medium flex items-center justify-center gap-2 shadow-lg transition-transform hover:scale-105"
                  style={{ 
                    backgroundColor: primaryTextColor === '#1F2937' ? branding.primaryColor : branding.secondaryColor,
                    color: primaryTextColor === '#1F2937' ? primaryTextColor : secondaryTextColor,
                    fontFamily: data.fonts?.body || 'Inter'
                  }}
                >
                  <ExternalLink className="w-5 h-5" />
                  {data.buttonText || 'Website besuchen'}
                </button>

                {/* Company name at bottom */}
                {branding.brandingType !== 'none' && data.companyPosition === 'bottom' && (
                  <div className="text-center mt-8">
                    {branding.brandingType === 'beyondpages' ? (
                      <p className="text-sm opacity-70" style={{ 
                        color: secondaryTextColor,
                        fontFamily: data.fonts?.body || 'Inter'
                      }}>
                        Powered by BeyondPages
                      </p>
                    ) : branding.brandingType === 'custom' && branding.companyName ? (
                      <h2 className="text-lg font-semibold" style={{ 
                        color: secondaryTextColor,
                        fontFamily: data.fonts?.body || 'Inter'
                      }}>
                        {branding.companyName}
                      </h2>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}